import { resolveSymbolLookupButtonClickAction } from './symbol-lookup-button-click-resolver';
import { resolveSymbolLookupInputChange, SymbolLookupInputChangeResolverOptions } from './symbol-lookup-input-change-resolver';
import { resolveSymbolLookupSelection, SymbolLookupSelectionResolverOptions } from './symbol-lookup-selection-resolver';
import type { AutoSuggestSymbol } from '../../domain/symbol-lookup.domain';
import type { SecuritySymbol } from '../../domain/symbol.domain';

export type SymbolLookupInputModelEffect = 'setLenientModel' | 'clearCheckedInvalidModel';

export type SymbolLookupInputChangePlan =
  | {
    readonly kind: 'empty';
    readonly emitQuery: '';
  }
  | {
    readonly kind: 'text';
    readonly queryValue: string;
    readonly modelEffects: ReadonlyArray<SymbolLookupInputModelEffect>;
  };

export interface SymbolLookupButtonClickPlan {
  readonly kind: 'none' | 'validateAndSelect' | 'markTouched';
  readonly shouldCheckSymbol: boolean;
  readonly shouldUpdateValidity: true;
}

export type SymbolLookupSelectionPlan =
  | {
    readonly kind: 'noop';
  }
  | {
    readonly kind: 'select';
    readonly selected?: SecuritySymbol | AutoSuggestSymbol;
  }
  | {
    readonly kind: 'setModelThenSelect';
    readonly nextModel: SecuritySymbol | AutoSuggestSymbol | undefined;
    readonly selected: undefined;
  };

/**
 * Translate low-level resolver outcomes into deterministic orchestration plans
 * so the component can stay as a thin side-effect adapter.
 */
export function planSymbolLookupInputChange(
  options: SymbolLookupInputChangeResolverOptions
): SymbolLookupInputChangePlan {
  const resolution = resolveSymbolLookupInputChange(options);

  if (resolution.kind === 'empty') {
    return resolution;
  }

  switch (resolution.modelAction) {
    case 'setLenientModelThenClearCheckedInvalidModel':
      return {
        kind: 'text',
        queryValue: resolution.queryValue,
        modelEffects: ['setLenientModel', 'clearCheckedInvalidModel']
      };
    case 'setLenientModel':
      return {
        kind: 'text',
        queryValue: resolution.queryValue,
        modelEffects: ['setLenientModel']
      };
    case 'clearCheckedInvalidModel':
      return {
        kind: 'text',
        queryValue: resolution.queryValue,
        modelEffects: ['clearCheckedInvalidModel']
      };
    default:
      return {
        kind: 'text',
        queryValue: resolution.queryValue,
        modelEffects: []
      };
  }
}

export function planSymbolLookupButtonClick(
  disabled: boolean,
  hasInputValue: boolean,
  hasSymbols: boolean
): SymbolLookupButtonClickPlan {
  const action = resolveSymbolLookupButtonClickAction({
    disabled,
    hasInputValue,
    hasSymbols
  });

  return {
    kind: action.kind,
    // `checkSymbol` arms the SymbolLookupValidatorDirective, which reports `invalid` when
    // the input is non-empty but no symbol was resolved. Only arm it when the click can
    // actually resolve a symbol (`validateAndSelect`). Arming it for `markTouched` — the
    // no-matches case — surfaced a validation error while the user was still typing.
    // See wiki/signal-forms/core-signal-forms-revised-dual-support-plan.md (Phase B4).
    shouldCheckSymbol: action.kind === 'validateAndSelect',
    shouldUpdateValidity: true
  };
}

export function planSymbolLookupSelection(
  options: SymbolLookupSelectionResolverOptions
): SymbolLookupSelectionPlan {
  const resolution = resolveSymbolLookupSelection(options);

  switch (resolution.kind) {
    case 'setModel':
      return {
        kind: 'setModelThenSelect',
        nextModel: resolution.nextModel,
        selected: undefined
      };
    case 'select':
      return {
        kind: 'select',
        selected: resolution.selected
      };
    default:
      return { kind: 'noop' };
  }
}

